import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Loader2, AlertCircle, CheckCircle2, Circle, AlertTriangle, Download,
  Send, Mail, Search, BadgeCheck, Clock, Upload, FileCheck2, RefreshCw,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";
import { PledgeShell, PledgeCard, pledgeErrorMessage } from "../components/PledgeShell";

/**
 * Where a student finds out what has happened to their declaration.
 *
 * Two jobs in one page, decided by whether the URL carries a token:
 *
 *   /graduate-pledge/status/{token}   the record itself, reached from the
 *                                     link in their email
 *   /graduate-pledge/status           "I have lost the email" — takes an
 *                                     address and a matric number and asks
 *                                     the server to send the link
 *
 * The lookup never shows anything. It answers with the same sentence whether
 * or not it found a record, and the link goes to the inbox — so reading a
 * record requires controlling the mailbox it was submitted with. That is the
 * whole reason there is no "enter your Form No." box here: form numbers run
 * NIQS/GPP/2026/0001 upwards and would read out the cohort to anyone counting.
 *
 * Plain `fetch`, not the RTK slice: that slice attaches a bearer token and
 * force-redirects to /login on a 401, and these students have no account.
 */

type Stage = {
  key: string;
  label: string;
  /** `review` = uploaded but not yet checked — not a half-done, its own state. */
  state: "done" | "pending" | "missed" | "review";
  detail: string | null;
};

type HodApproval = {
  status: "pending" | "approved" | "declined";
  institution: string | null;
  hod_name: string | null;
  decided_at: string | null;
  notes: string | null;
  /** The department has nobody in the portal yet, which is why it is slow. */
  awaiting_link: boolean;
};

type StatusData = {
  form_no: string;
  name: string;
  institution: string;
  matric_no: string;
  programme: string;
  graduation: string;
  email: string;
  phone: string;
  state_after_graduation: string;
  interest: string;
  student_membership_no: string | null;
  submitted_at: string | null;
  pdf_url: string;
  stages: Stage[];
  membership_no_issued: string | null;
  hod: HodApproval;
  cohort: { name: string; year: number; ceremony_date: string | null } | null;
};

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-800 " +
  "focus:outline-none focus:ring-2 focus:ring-[#0b2e6b]/30 focus:border-[#0b2e6b] bg-white";

function StageIcon({ state }: { state: Stage["state"] }) {
  if (state === "done") return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
  // With us, not yet looked at. Distinct from both "your turn" and "done".
  if (state === "review") return <Clock className="w-5 h-5 text-sky-500" />;
  // Amber, not red: "not recorded as attending" is something to take up with
  // the Secretariat, not a verdict to shout at a graduate.
  if (state === "missed") return <AlertTriangle className="w-5 h-5 text-amber-500" />;
  return <Circle className="w-5 h-5 text-slate-300" />;
}

export default function PublicGraduatePledgeStatusPage() {
  const { token } = useParams<{ token?: string }>();

  return token
    ? <StatusView token={token} />
    : <LookupView />;
}

/* ── The record ─────────────────────────────────────────────────────────── */

function StatusView({ token }: { token: string }) {
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BASE_URL}/graduate-pledge/status/${token}`, {
        headers: { Accept: "application/json" },
      });
      if (res.status === 404) {
        throw new Error("That link does not match any submission. It may have been mistyped.");
      }
      if (!res.ok) throw new Error(`Your record could not be loaded (HTTP ${res.status}).`);
      const json = await res.json();
      setData(json?.data ?? null);
    } catch (e: any) {
      setError(e?.message || "Your record could not be loaded. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resend = async () => {
    setResending(true);
    setResent("");
    try {
      const res = await fetch(`${BASE_URL}/graduate-pledge/forms/${token}/resend`, {
        method: "POST",
        headers: { Accept: "application/json" },
      });
      const json = await res.json().catch(() => ({}));
      setResent(res.ok
        ? (json?.message ?? "Sent.")
        : pledgeErrorMessage(json?.message, "Could not send the email."));
    } catch {
      setResent("Could not send the email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <PledgeShell subtitle="Graduate Pledge — your status" width="max-w-2xl">
        <PledgeCard className="p-10 flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 text-[#0b2e6b] animate-spin" />
          <p className="text-sm text-slate-500">Loading your record…</p>
        </PledgeCard>
      </PledgeShell>
    );
  }

  if (error || !data) {
    return (
      <PledgeShell subtitle="Graduate Pledge — your status" width="max-w-2xl">
        <PledgeCard className="p-8 text-center space-y-4">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
          <p className="text-sm text-slate-600">{error}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button onClick={load} className="text-sm font-semibold text-[#0b2e6b] underline">
              Try again
            </button>
            <Link to="/graduate-pledge/status" className="text-sm font-semibold text-[#0b2e6b] underline">
              Email me my link
            </Link>
          </div>
        </PledgeCard>
      </PledgeShell>
    );
  }

  return (
    <PledgeShell subtitle="Graduate Pledge — your status" width="max-w-2xl">
      <div className="space-y-5">

        <PledgeCard className="px-6 py-5 text-center">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Form No.
          </div>
          <div className="text-2xl font-black text-[#0b2e6b] tracking-wide mt-1">
            {data.form_no}
          </div>
          <div className="text-sm font-semibold text-slate-700 mt-2">{data.name}</div>
          <div className="text-xs text-slate-400">{data.institution}</div>
        </PledgeCard>

        {/* The thing they came for, once it exists. */}
        {data.membership_no_issued && (
          <PledgeCard className="px-6 py-5 border-emerald-200 bg-emerald-50">
            <div className="flex items-center gap-3">
              <BadgeCheck className="w-8 h-8 text-emerald-600 shrink-0" />
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                  Membership number issued
                </div>
                <div className="text-xl font-black text-emerald-900">
                  {data.membership_no_issued}
                </div>
              </div>
            </div>
          </PledgeCard>
        )}

        <PledgeCard>
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <span className="text-sm font-bold text-slate-800">Progress</span>
          </div>
          <ol className="p-5 space-y-4">
            {data.stages.map((s, i) => (
              <li key={s.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <StageIcon state={s.state} />
                  {/* Joins one stage to the next; absent after the last. */}
                  {i < data.stages.length - 1 && (
                    <div className="w-px flex-1 bg-slate-200 mt-1" style={{ minHeight: 18 }} />
                  )}
                </div>
                <div className="pb-1">
                  <div
                    className={`text-sm font-semibold ${
                      s.state === "pending" ? "text-slate-400" : "text-slate-800"
                    }`}
                  >
                    {s.label}
                  </div>
                  {s.detail && <div className="text-xs text-slate-500 mt-0.5">{s.detail}</div>}
                </div>
              </li>
            ))}
          </ol>
        </PledgeCard>

        <HodPanel hod={data.hod} />

        <PledgeCard>
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
            <span className="text-sm font-bold text-slate-800">What you submitted</span>
          </div>
          <dl className="px-5 py-3 text-sm divide-y divide-slate-100">
            {([
              ["Matriculation no.", data.matric_no],
              ["Programme", data.programme],
              ["Expected graduation", data.graduation],
              ["Student membership no.", data.student_membership_no || "Not yet a member"],
              ["Email", data.email],
              ["Phone", data.phone],
              ["State after graduation", data.state_after_graduation],
              ["Area of QS interest", data.interest],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-2">
                <dt className="text-slate-400">{k}</dt>
                <dd className="font-semibold text-slate-800 text-right">{v}</dd>
              </div>
            ))}
          </dl>
          <div className="px-5 pb-4 text-[11px] text-slate-400">
            If any detail is wrong, email the Secretariat quoting your Form No. — do not fill
            the form in again.
          </div>
        </PledgeCard>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={data.pdf_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 rounded-lg bg-[#0b2e6b] text-white
                       px-4 py-3 text-sm font-bold hover:bg-[#0a2558]"
          >
            <Download className="w-4 h-4" /> Download my form
          </a>
          <button
            onClick={resend}
            disabled={resending}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300
                       bg-white px-4 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50
                       disabled:opacity-50"
          >
            {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Email it to me
          </button>
        </div>

        {resent && (
          <div className="text-center text-xs text-slate-500">{resent}</div>
        )}
      </div>
    </PledgeShell>
  );
}

/* ── Where the Head of Department's confirmation stands ────────────────── */

/**
 * The student has NOTHING TO DO here, and the panel's job is to say so.
 *
 * The old version of this screen asked them to print, sign, collect a stamp
 * and upload a photograph. That is gone: their Head of Department confirms
 * them in the portal. What is left is a progress report — which matters,
 * because an application that looks idle is one a student chases the wrong
 * office about.
 */
function HodPanel({ hod }: { hod: HodApproval }) {
  const approved = hod.status === "approved";
  const declined = hod.status === "declined";

  return (
    <PledgeCard>
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex items-center justify-between">
        <span className="text-sm font-bold text-slate-800">Head of Department</span>
        <span
          className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border ${
            approved ? "text-emerald-700 bg-emerald-50 border-emerald-200"
            : declined ? "text-amber-700 bg-amber-50 border-amber-200"
            : "text-sky-700 bg-sky-50 border-sky-200"
          }`}
        >
          {approved ? "Confirmed" : declined ? "Not confirmed" : "Awaiting"}
        </span>
      </div>

      <div className="p-5 space-y-3">
        {declined && hod.notes && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 mb-1">
              What your department said
            </div>
            <p className="text-sm text-amber-900 leading-relaxed">{hod.notes}</p>
            <p className="text-xs text-amber-800 mt-2">
              Take this up with your department &mdash; they can confirm you once it is sorted.
            </p>
          </div>
        )}

        {approved && (
          <p className="text-sm text-slate-700 leading-relaxed">
            {hod.hod_name || "Your Head of Department"} has confirmed that you are a bona fide
            final-year student of {hod.institution}. Nothing further is needed from you before
            the ceremony.
          </p>
        )}

        {!approved && !declined && (
          <>
            <p className="text-sm text-slate-700 leading-relaxed">
              Your application has gone to the Head of Department at {hod.institution} for
              confirmation. <strong>There is nothing for you to do</strong> &mdash; no printing,
              no signature, no stamp.
            </p>
            {hod.awaiting_link && (
              <p className="text-xs text-slate-500 leading-relaxed bg-slate-50 border rounded-lg px-3 py-2">
                Your department does not yet have an account on the NIQS portal, so the
                Secretariat will confirm this on their behalf. It may take a little longer.
              </p>
            )}
          </>
        )}
      </div>
    </PledgeCard>
  );
}

/* ── Lost the email ─────────────────────────────────────────────────────── */

function LookupView() {
  const [email, setEmail] = useState("");
  const [matric, setMatric] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setError("");

    if (!email.trim() || !matric.trim()) {
      setError("Enter both your email address and your matriculation number.");
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${BASE_URL}/graduate-pledge/status/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email.trim(), matric_no: matric.trim() }),
      });
      const json = await res.json().catch(() => ({}));

      if (res.status === 422) {
        setError(json?.message || "Please check what you have entered.");
        return;
      }
      if (!res.ok) {
        throw new Error(pledgeErrorMessage(json?.message, `Request failed (HTTP ${res.status}).`));
      }

      // Deliberately the server's own wording — it says "if those details
      // match", and softening that into "we have sent it" would turn this
      // screen into a way of testing whether somebody is in the cohort.
      setSent(json?.message ?? "If those details match a submission, we have emailed the link.");
    } catch (e: any) {
      setError(e?.message || "That could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PledgeShell subtitle="Graduate Pledge — find my form" width="max-w-md">
      <PledgeCard className="p-6">
        {sent ? (
          <div className="text-center space-y-3">
            <Mail className="w-8 h-8 text-emerald-600 mx-auto" />
            <p className="text-sm text-slate-600 leading-relaxed">{sent}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <p className="text-sm text-slate-600 leading-relaxed">
              Enter the email address and matriculation number you submitted, and we will
              email you the link to your form and its status.
            </p>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                Email address
              </label>
              <input
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                Matriculation number
              </label>
              <input
                className={inputCls}
                value={matric}
                onChange={(e) => setMatric(e.target.value)}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200
                              bg-red-50 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={sending}
              className="w-full rounded-lg bg-[#0b2e6b] text-white px-4 py-3 text-sm font-bold
                         hover:bg-[#0a2558] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {sending ? "Sending…" : "Email me the link"}
            </button>

            <p className="text-center text-[11px] text-slate-400">
              Not submitted yet?{" "}
              <Link to="/graduate-pledge" className="text-[#0b2e6b] underline font-semibold">
                Fill in the declaration
              </Link>
            </p>
          </form>
        )}
      </PledgeCard>
    </PledgeShell>
  );
}
